/* ============================================================================
 * SYLONI® · Magnetic CTA (Sprint 2)
 *
 * 鼠标接近主 CTA 按钮时按钮被吸引偏移，鼠标离开弹回。
 * + 鼠标位置在按钮表面投射光斑（CSS 变量 --mx / --my）。
 *
 * Application:
 *   - Explicit opt-in: <button data-syloni-magnetic> ...
 *   - Sprint 1 hero CTA already has data-syloni-magnetic
 *
 * Per-element configuration via data attributes (optional):
 *   - data-magnetic-strength (default 0.35) · 偏移系数
 *   - data-magnetic-radius (default 180)    · 触发半径 px
 *
 * Auto-disabled on:
 *   - prefers-reduced-motion: reduce
 *   - low-end devices (.syloni-no-motion class on <html>)
 *   - touch-primary devices (.syloni-touch-device class on <html>)
 *
 * Depends on: assets/syloni-motion-base.js (window.SYLONI globals)
 *
 * Last update: 2026-04-27 · Sprint 2 v0.3.0
 * ============================================================================ */

(function () {
  'use strict';

  const SELECTOR = '[data-syloni-magnetic]';
  const STRENGTH_DEFAULT = 0.35;
  const RADIUS_DEFAULT = 180;

  const instances = new WeakMap();

  // ───────────────────────────────────────────────────────────────
  // MagneticButton · single-instance controller
  // ───────────────────────────────────────────────────────────────
  class MagneticButton {
    constructor(el) {
      this.el = el;
      this.strength = parseFloat(el.dataset.magneticStrength) || STRENGTH_DEFAULT;
      this.radius = parseInt(el.dataset.magneticRadius, 10) || RADIUS_DEFAULT;

      // Use a parent zone the size of (button + radius padding) for hit testing.
      // We listen on document so attraction can start before pointer enters bounds.
      this.targetX = 0;
      this.targetY = 0;
      this.currentX = 0;
      this.currentY = 0;
      this.rafId = null;
      this.tracking = false;

      this._boundMove = this._handleMove.bind(this);
      this._boundLeave = this._handleLeave.bind(this);
      this._boundClick = this._handleClick.bind(this);

      // Attach light spot CSS hooks
      el.classList.add('syloni-magnetic-btn');

      document.addEventListener('mousemove', this._boundMove, { passive: true });
      el.addEventListener('mouseleave', this._boundLeave);
      el.addEventListener('click', this._boundClick);
    }

    _handleMove(e) {
      const rect = this.el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);

      if (dist < this.radius) {
        const factor = (1 - dist / this.radius) * this.strength;
        this.targetX = dx * factor;
        this.targetY = dy * factor;

        // Light spot mouse position (pixels relative to button)
        const localX = e.clientX - rect.left;
        const localY = e.clientY - rect.top;
        this.el.style.setProperty('--syloni-mx', localX + 'px');
        this.el.style.setProperty('--syloni-my', localY + 'px');
        this.el.style.setProperty('--syloni-mag-active', '1');

        if (!this.tracking) {
          this.tracking = true;
          this._tick();
        }
      } else if (this.tracking) {
        // Out of radius — start snap-back
        this.targetX = 0;
        this.targetY = 0;
        this.el.style.setProperty('--syloni-mag-active', '0');
      }
    }

    _handleLeave() {
      this.targetX = 0;
      this.targetY = 0;
      this.el.style.setProperty('--syloni-mag-active', '0');
    }

    _handleClick() {
      // Brief scale bounce on click (independent of magnet position)
      this.el.classList.remove('syloni-magnetic-pulse');
      // Force reflow to restart animation
      void this.el.offsetWidth;
      this.el.classList.add('syloni-magnetic-pulse');
    }

    _tick() {
      // Smooth interpolation toward target
      const ease = 0.18;
      this.currentX += (this.targetX - this.currentX) * ease;
      this.currentY += (this.targetY - this.currentY) * ease;

      this.el.style.transform = `translate3d(${this.currentX.toFixed(2)}px, ${this.currentY.toFixed(2)}px, 0)`;

      // Stop when close to rest position
      const stillMoving =
        Math.abs(this.currentX - this.targetX) > 0.1 ||
        Math.abs(this.currentY - this.targetY) > 0.1;

      if (stillMoving || (this.targetX !== 0 || this.targetY !== 0)) {
        this.rafId = requestAnimationFrame(() => this._tick());
      } else {
        // Settled
        this.el.style.transform = '';
        this.tracking = false;
        this.rafId = null;
      }
    }

    destroy() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      document.removeEventListener('mousemove', this._boundMove);
      this.el.removeEventListener('mouseleave', this._boundLeave);
      this.el.removeEventListener('click', this._boundClick);
      this.el.style.transform = '';
      this.el.classList.remove('syloni-magnetic-btn', 'syloni-magnetic-pulse');
    }
  }

  // ───────────────────────────────────────────────────────────────
  // Capability gate · skip on touch / reduced motion / low-end
  // ───────────────────────────────────────────────────────────────
  function shouldSkip() {
    const html = document.documentElement;
    return (
      html.classList.contains('syloni-no-motion') ||
      html.classList.contains('syloni-touch-device') ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  // ───────────────────────────────────────────────────────────────
  // Auto-init
  // ───────────────────────────────────────────────────────────────
  function initAll(scope) {
    if (shouldSkip()) return;
    const root = scope || document;
    const els = root.querySelectorAll(SELECTOR);
    els.forEach((el) => {
      if (instances.has(el)) return;
      const inst = new MagneticButton(el);
      instances.set(el, inst);
    });
  }

  function destroyEl(el) {
    const inst = instances.get(el);
    if (inst) {
      inst.destroy();
      instances.delete(el);
    }
  }

  // DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Theme editor lifecycle
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelector) initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    if (e.target) {
      const els = e.target.querySelectorAll
        ? e.target.querySelectorAll(SELECTOR)
        : [];
      els.forEach(destroyEl);
    }
  });

  // Expose for debugging
  if (window.SYLONI) {
    window.SYLONI.modules.magnetic = {
      version: '0.3.0',
      initAll,
      destroyEl,
      instances,
    };
  }

  console.log(
    '%c SYLONI magnetic v0.3.0 ',
    'background:#D4AF37;color:#0A0A0A;padding:3px 8px;font-family:monospace;letter-spacing:2px;font-weight:600;'
  );
})();
